/**
 * OrderDetailPage — Vue detaillee d'une commande avec timeline et actions
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCustomer } from '../../contexts/CustomerContext';
import { ecommerceApi } from '../../api/ecommerce.api';
import type { Order, OrderStatus, OrderRefund } from '../../types/ecommerce.types';
import { commentaireClient } from '../../utils/commentaireClient';

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bg: string }> = {
  pending: { label: 'En attente', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  confirmed: { label: 'Confirmee', color: 'text-blue-700', bg: 'bg-blue-100' },
  preparing: { label: 'En preparation', color: 'text-purple-700', bg: 'bg-purple-100' },
  ready: { label: 'Prete', color: 'text-indigo-700', bg: 'bg-indigo-100' },
  delivering: { label: 'En livraison', color: 'text-cyan-700', bg: 'bg-cyan-100' },
  completed: { label: 'Livree', color: 'text-green-700', bg: 'bg-green-100' },
  cancelled: { label: 'Annulee', color: 'text-red-700', bg: 'bg-red-100' },
};

const STATUS_TIMELINE: OrderStatus[] = ['pending', 'confirmed', 'preparing', 'ready', 'completed'];

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { customer } = useCustomer();

  const [order, setOrder] = useState<Order | null>(null);
  const [refunds, setRefunds] = useState<OrderRefund[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  useEffect(() => {
    if (!customer) { navigate('/shop/login'); return; }
    if (!orderId) { navigate('/shop/orders'); return; }
    loadOrder();
  }, [customer, orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const [orderData, refundsData] = await Promise.all([
        ecommerceApi.orders.getOrderById(orderId!),
        ecommerceApi.orders.getOrderRefunds(orderId!).catch(() => []),
      ]);
      setOrder(orderData);
      setRefunds(Array.isArray(refundsData) ? refundsData : []);
    } catch (err) {
      console.error('Erreur chargement commande:', err);
      navigate('/shop/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!order) return;
    setCancelling(true);
    try {
      await ecommerceApi.orders.cancelOrder(order.id);
      await loadOrder();
      setShowCancelModal(false);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur lors de l\'annulation');
    } finally {
      setCancelling(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!orderId) return;
    try {
      setLoadingInvoice(true);
      const html = await ecommerceApi.orders.getInvoice(orderId);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(html);
        w.document.close();
        w.print();
      }
    } catch (err) {
      console.error('Erreur generation facture:', err);
      alert('Impossible de generer la facture.');
    } finally {
      setLoadingInvoice(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const canCancel = order && ['pending', 'confirmed'].includes(order.status);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600" />
      </div>
    );
  }

  if (!order) return null;

  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const currentStepIndex = STATUS_TIMELINE.indexOf(order.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button onClick={() => navigate('/shop/orders')} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Mes commandes
          </button>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{order.order_number}</h1>
              <p className="text-gray-600">{formatDate(order.created_at)}</p>
            </div>
            <span className={`px-4 py-2 rounded-full font-bold text-lg ${statusConfig.color} ${statusConfig.bg}`}>
              {statusConfig.label}
            </span>
          </div>
        </div>

        {/* Timeline */}
        {order.status !== 'cancelled' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Suivi de commande</h2>
            <div className="flex items-center justify-between">
              {STATUS_TIMELINE.map((step, idx) => {
                const stepConfig = STATUS_CONFIG[step];
                const isActive = idx <= currentStepIndex;
                const isCurrent = step === order.status;
                return (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        isCurrent ? 'bg-green-600 text-white ring-4 ring-green-200' :
                        isActive ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                      }`}>
                        {isActive ? '\u2713' : idx + 1}
                      </div>
                      <span className={`text-xs mt-1 ${isActive ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                        {stepConfig.label}
                      </span>
                    </div>
                    {idx < STATUS_TIMELINE.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 ${idx < currentStepIndex ? 'bg-green-500' : 'bg-gray-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cancelled info */}
        {order.status === 'cancelled' && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-6">
            <h2 className="text-xl font-bold text-red-800 mb-2">Commande annulee</h2>
            {order.cancellation_reason && <p className="text-red-700">{order.cancellation_reason}</p>}
            {order.cancelled_at && <p className="text-red-600 text-sm mt-1">Le {formatDate(order.cancelled_at)}</p>}
          </div>
        )}

        {/* Order details */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Detail de la commande</h2>

          <div className="space-y-3">
            {order.lines.map(line => (
              <div key={line.id} className="flex items-center justify-between py-3 border-b last:border-0">
                <div className="flex-1">
                  <div className="font-medium">{line.product_name}{(line as any).prep_options?.includes('coupé') ? ' · coupé' : ''}</div>
                  <div className="text-sm text-gray-600">
                    {line.quantity_unit === 'grams' && line.weight_grams
                      ? `${(line.quantity / 1000).toFixed(2)} kg`
                      : `x${line.quantity}`}
                    {' '}&mdash; {(line.unit_price_cents / 100).toFixed(2)} &euro;/{line.quantity_unit === 'grams' ? 'kg' : 'unite'}
                  </div>
                  {line.prepared && <span className="text-xs text-green-700 font-medium">Prepare</span>}
                </div>
                <div className="font-bold text-lg">{(line.total_price_cents / 100).toFixed(2)} &euro;</div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mt-4 pt-4 border-t-2 space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Sous-total HT</span>
              <span>{(order.subtotal_cents / 100).toFixed(2)} &euro;</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>TVA</span>
              <span>{(order.tax_cents / 100).toFixed(2)} &euro;</span>
            </div>
            {order.delivery_fee_cents > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Frais de livraison</span>
                <span>{(order.delivery_fee_cents / 100).toFixed(2)} &euro;</span>
              </div>
            )}
            {order.discount_cents > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Reduction</span>
                <span>-{(order.discount_cents / 100).toFixed(2)} &euro;</span>
              </div>
            )}
            <div className="flex justify-between text-2xl font-bold pt-2 border-t">
              <span>Total</span>
              <span className="text-green-700">{(order.total_cents / 100).toFixed(2)} &euro;</span>
            </div>
          </div>
        </div>

        {/* Info boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-bold text-gray-800 mb-3">Livraison</h3>
            <p className="text-gray-600">
              {order.delivery_option === 'delivery' ? 'Livraison a domicile' : 'Retrait en magasin'}
            </p>
            {order.delivery_date && <p className="text-gray-600">{formatDate(order.delivery_date)}</p>}
            {order.delivery_slot && <p className="text-gray-600">{order.delivery_slot}</p>}
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-bold text-gray-800 mb-3">Paiement</h3>
            <p className="text-gray-600">
              {order.payment_method === 'wallet' ? 'Mes FOLIES' : order.payment_method === 'card' ? 'Carte bancaire' : 'Especes'}
            </p>
            <p className={`font-medium ${order.payment_status === 'paid' ? 'text-green-700' : 'text-yellow-600'}`}>
              {order.payment_status === 'paid' ? 'Paye' : order.payment_status === 'failed' ? 'Echec du paiement' : 'En attente'}
            </p>
          </div>
        </div>

        {/* Notes */}
        {commentaireClient(order.notes) && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-2">Notes</h3>
            <p className="text-gray-600 whitespace-pre-line">{commentaireClient(order.notes)}</p>
          </div>
        )}

        {/* Refunds */}
        {refunds.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-3">Remboursements</h3>
            <div className="space-y-3">
              {refunds.map(refund => (
                <div key={refund.id} className="p-4 border-2 border-green-200 bg-green-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-medium">{refund.refund_number}</span>
                      <p className="text-sm text-gray-600 mt-1">{refund.reason}</p>
                      <p className="text-xs text-gray-600">{formatDate(refund.created_at)}</p>
                    </div>
                    <div className="text-green-700 font-bold text-lg">
                      +{(refund.amount_cents / 100).toFixed(2)} &euro;
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={handleDownloadInvoice}
            disabled={loadingInvoice}
            className="px-6 py-3 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {loadingInvoice ? 'Generation...' : 'Telecharger la facture'}
          </button>

          <button
            onClick={() => window.print()}
            className="px-6 py-3 bg-white border-2 border-gray-300 rounded-xl font-bold hover:bg-gray-50 transition-colors"
          >
            Imprimer
          </button>

          {canCancel && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="px-6 py-3 bg-red-50 border-2 border-red-300 text-red-700 rounded-xl font-bold hover:bg-red-100 transition-colors"
            >
              Annuler la commande
            </button>
          )}

          {order.delivery_option === 'delivery' && order.status === 'delivering' && (
            <Link
              to={`/shop/track/${order.id}`}
              className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors"
            >
              Suivre la livraison
            </Link>
          )}
        </div>

        {/* Cancel modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setShowCancelModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8" onClick={e => e.stopPropagation()}>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Annuler la commande ?</h2>
              <p className="text-gray-600 mb-6">
                Cette action est irreversible.
                {order.payment_method === 'wallet' && ' Le montant sera recredite sur votre compte prepaye.'}
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowCancelModal(false)} className="flex-1 py-3 border-2 border-gray-300 rounded-lg font-bold hover:bg-gray-50">
                  Non, garder
                </button>
                <button onClick={handleCancel} disabled={cancelling} className="flex-1 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:bg-gray-400">
                  {cancelling ? 'Annulation...' : 'Oui, annuler'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
